import { api } from '@/lib/api/client';
import type {
  AuthUser,
  ChangePasswordInput,
  LoginCredentials,
  Profile,
  ResetPasswordInput,
  Role,
} from './types';
import { isRole } from './types';

/**
 * Auth endpoint bindings. All on ingestion (:8001).
 *
 * Responses are parsed defensively rather than cast: these payloads decide who the
 * user is and what they can see, so a shape surprise must fail loudly here instead of
 * silently producing a user with an undefined role.
 */

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function readRole(value: unknown): Role {
  if (isRole(value)) return value;
  // An unknown role gets the least privilege rather than the benefit of the doubt.
  return 'user';
}

function readUser(raw: unknown): AuthUser {
  if (typeof raw !== 'object' || raw === null) {
    throw new Error('The sign-in response did not include a user.');
  }
  const source = raw as Record<string, unknown>;
  const userId = source['user_id'];
  if (typeof userId !== 'number') {
    throw new Error('The sign-in response did not include a user id.');
  }
  return {
    userId,
    username: asString(source['username']) ?? '',
    role: readRole(source['role']),
    fullName: asString(source['full_name']),
    mustChangePassword: source['must_change_password'] === true,
  };
}

export interface LoginResult {
  readonly token: string;
  readonly user: AuthUser;
}

export async function login(credentials: LoginCredentials): Promise<LoginResult> {
  const body = await api.post<unknown>('ingestion', '/api/v1/auth/login', {
    body: { username: credentials.username, password: credentials.password },
  });

  if (typeof body !== 'object' || body === null) {
    throw new Error('Unexpected sign-in response.');
  }
  const source = body as Record<string, unknown>;
  const token = asString(source['token']);
  if (token === null) {
    throw new Error('The sign-in response did not include a token.');
  }

  return { token, user: readUser(source['user']) };
}

/**
 * Current profile.
 *
 * A 401 here is the backend's only revocation check — it fires when the account was
 * deactivated after the token was issued.
 */
export async function fetchProfile(signal?: AbortSignal): Promise<Profile> {
  const body = await api.get<unknown>('ingestion', '/api/v1/auth/me', {
    ...(signal ? { signal } : {}),
  });

  const user = readUser(body);
  const source = body as Record<string, unknown>;

  return {
    ...user,
    email: asString(source['email']),
    officialEmail: asString(source['official_email']),
    isActive: source['is_active'] !== false,
  };
}

export async function changePassword(input: ChangePasswordInput): Promise<void> {
  await api.post('ingestion', '/api/v1/auth/change-password', {
    // current_password is verified even on the forced first change; null returns 400.
    body: { current_password: input.currentPassword, new_password: input.newPassword },
  });
}

export type ForgotPasswordResult =
  | { readonly status: 'sent' }
  | { readonly status: 'not_found' }
  /** Development mode only — the backend hands back the token when email is disabled. */
  | { readonly status: 'ok'; readonly resetUrl: string | null };

export async function forgotPassword(email: string): Promise<ForgotPasswordResult> {
  const body = await api.post<unknown>('ingestion', '/api/v1/auth/forgot-password', {
    body: { email },
  });

  const source = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>;
  const status = source['status'];

  if (status === 'not_found') return { status: 'not_found' };
  if (status === 'ok') return { status: 'ok', resetUrl: asString(source['reset_url']) };
  return { status: 'sent' };
}

export async function resetPassword(input: ResetPasswordInput): Promise<void> {
  await api.post('ingestion', '/api/v1/auth/reset-password', {
    body: { token: input.token, new_password: input.newPassword },
  });
}

export interface UpdateProfileInput {
  readonly fullName?: string | undefined;
  readonly officialEmail?: string | undefined;
}

/** Cannot change role, active state, username or login email. */
export async function updateProfile(input: UpdateProfileInput): Promise<void> {
  const body: Record<string, unknown> = {};
  if (input.fullName !== undefined) body['full_name'] = input.fullName;
  if (input.officialEmail !== undefined) body['official_email'] = input.officialEmail;

  await api.patch('ingestion', '/api/v1/auth/me', { body });
}
